import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Print as PrintIcon,
  Cancel as CancelIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import axios from 'axios';

const LabelManagement = () => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    recipientName: 'John Doe',
    recipientAddress: '6600 N Military Trail',
    recipientCity: 'Boca Raton',
    recipientState: 'FL',
    recipientZip: '33496',
    recipientCountry: 'US',
    weight: 1,
    length: 10,
    width: 8,
    height: 6,
    serviceType: '',
    shipDate: new Date()
  });

  const serviceTypes = [
    { label: 'FedEx Ground', value: 'FEDEX_GROUND' },
    { label: 'FedEx 2Day', value: 'FEDEX_2_DAY' },
    { label: 'FedEx Express', value: 'FEDEX_EXPRESS' },
    { label: 'FedEx Overnight', value: 'FEDEX_OVERNIGHT' }
  ];

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateLabel = async () => {
    setLoading(true);
    try {
      // Use API token from environment for Shipium API calls
      const apiToken = process.env.REACT_APP_API_TOKEN;
      const apiAuthorization = apiToken 
        ? (apiToken.startsWith('Basic ') ? apiToken : `Basic ${apiToken}`)
        : null;
      
      const response = await axios.post('/api/v1/labels/create', {
        currencyCode: 'USD',
        shipmentParameters: {
          testMode: true,
          orderItemQuantities: [
            {
              productId: 'test-product',
              quantity: 1
            }
          ],
          packagingType: {
            packagingMaterial: 'cardboard',
            packagingSizeName: 'custom',
            linearDimensions: {
              linearUnit: 'in',
              length: formData.length,
              width: formData.width,
              height: formData.height
            },
            packagingWeight: {
              weight: formData.weight,
              weightUnit: 'lb'
            }
          },
          totalWeight: {
            weight: formData.weight,
            weightUnit: 'lb'
          },
          shipFromAddress: {
            name: 'Test Sender',
            street1: '123 Test St',
            city: 'Test City',
            state: 'CA',
            postalCode: '90210',
            countryCode: 'US'
          },
          destinationAddress: {
            name: formData.recipientName,
            street1: formData.recipientAddress,
            city: formData.recipientCity,
            state: formData.recipientState,
            postalCode: formData.recipientZip,
            countryCode: formData.recipientCountry
          }
        },
        labelParameters: {
          currencyCode: 'USD',
          labelFormats: ['zpl', 'pdf'],
          includeLabelImagesInResponse: true
        }
      }, {
        headers: {
          'Authorization': apiAuthorization
        }
      });
      
      showSnackbar('Label created successfully!', 'success');
      console.log('Label created:', response.data);
    } catch (error) {
      showSnackbar('Failed to create label', 'error');
      console.error('Error creating label:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReprintLabel = async () => {
    const shipiumLabelId = prompt('Enter Shipium Label ID to reprint:');
    if (!shipiumLabelId) return;

    setLoading(true);
    try {
      // Use API token from environment for Shipium API calls
      const apiToken = process.env.REACT_APP_API_TOKEN;
      const apiAuthorization = apiToken 
        ? (apiToken.startsWith('Basic ') ? apiToken : `Basic ${apiToken}`)
        : null;
      
      const response = await axios.get(`/api/v1/labels/reprint/${shipiumLabelId}`, {
        headers: {
          'Authorization': apiAuthorization
        }
      });
      
      showSnackbar('Label reprinted successfully!', 'success');
      console.log('Label reprinted:', response.data);
    } catch (error) {
      showSnackbar('Failed to reprint label', 'error');
      console.error('Error reprinting label:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVoidLabel = async () => {
    const shipiumLabelId = prompt('Enter Shipium Label ID to void:');
    if (!shipiumLabelId) return;

    setLoading(true);
    try {
      // Use API token from environment for Shipium API calls
      const apiToken = process.env.REACT_APP_API_TOKEN;
      const apiAuthorization = apiToken 
        ? (apiToken.startsWith('Basic ') ? apiToken : `Basic ${apiToken}`)
        : null;
      
      const response = await axios.post('/api/v1/labels/cancel', {
        shipiumLabelId
      }, {
        headers: {
          'Authorization': apiAuthorization
        }
      });
      
      showSnackbar('Label voided successfully!', 'success');
      console.log('Label voided:', response.data);
    } catch (error) {
      showSnackbar('Failed to void label', 'error');
      console.error('Error voiding label:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShippingIcon color="primary" />
          Label Management
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card>
              <CardHeader 
                title="Create New Label" 
                avatar={<AddIcon color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Recipient Name"
                      value={formData.recipientName}
                      onChange={(e) => handleInputChange('recipientName', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Address"
                      value={formData.recipientAddress}
                      onChange={(e) => handleInputChange('recipientAddress', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.recipientCity}
                      onChange={(e) => handleInputChange('recipientCity', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.recipientState}
                      onChange={(e) => handleInputChange('recipientState', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={formData.recipientZip}
                      onChange={(e) => handleInputChange('recipientZip', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Weight (lbs)"
                      type="number"
                      value={formData.weight}
                      onChange={(e) => handleInputChange('weight', parseFloat(e.target.value))}
                      inputProps={{ min: 0.1, max: 150, step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Service Type</InputLabel>
                      <Select
                        value={formData.serviceType}
                        onChange={(e) => handleInputChange('serviceType', e.target.value)}
                        label="Service Type"
                      >
                        {serviceTypes.map((service) => (
                          <MenuItem key={service.value} value={service.value}>
                            {service.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Length (in)"
                      type="number"
                      value={formData.length}
                      onChange={(e) => handleInputChange('length', parseInt(e.target.value))}
                      inputProps={{ min: 1, max: 108 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Width (in)"
                      type="number"
                      value={formData.width}
                      onChange={(e) => handleInputChange('width', parseInt(e.target.value))}
                      inputProps={{ min: 1, max: 108 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Height (in)"
                      type="number"
                      value={formData.height}
                      onChange={(e) => handleInputChange('height', parseInt(e.target.value))}
                      inputProps={{ min: 1, max: 108 }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Ship Date"
                      type="date"
                      value={formData.shipDate ? formData.shipDate.toISOString().split('T')[0] : ''}
                      onChange={(e) => handleInputChange('shipDate', new Date(e.target.value))}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                      onClick={handleCreateLabel}
                      disabled={loading}
                      sx={{ minWidth: 200 }}
                    >
                      {loading ? 'Creating...' : 'Create Label'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card>
              <CardHeader title="Label Actions" />
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={loading ? <CircularProgress size={20} /> : <PrintIcon />}
                    onClick={handleReprintLabel}
                    disabled={loading}
                    fullWidth
                  >
                    {loading ? 'Processing...' : 'Reprint Label'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="large"
                    startIcon={loading ? <CircularProgress size={20} /> : <CancelIcon />}
                    onClick={handleVoidLabel}
                    disabled={loading}
                    fullWidth
                  >
                    {loading ? 'Processing...' : 'Void Label'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default LabelManagement;
